import { Link } from 'react-router-dom';
import { WidgetCard } from '@/components/common/WidgetCard';
import { useQueueSummary } from '@/features/queue/hooks';
import { formatCount, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import { QueueListIcon } from '@/icons';

/** Mail queue depth by queue + oldest-deferred age, from /queue/summary. */
export function MailQueueWidget() {
  const { data, isLoading, isError, refetch } = useQueueSummary();

  const cells: Array<{ label: string; value: number; warn?: boolean }> = data
    ? [
        { label: 'Active', value: data.active },
        { label: 'Deferred', value: data.deferred, warn: data.deferred > 1000 },
        { label: 'Hold', value: data.hold },
        { label: 'Incoming', value: data.incoming },
      ]
    : [];

  const oldestIso = data && data.oldestDeferredAgeSeconds > 0
    ? new Date(Date.now() - data.oldestDeferredAgeSeconds * 1000).toISOString()
    : null;

  return (
    <WidgetCard
      title="Mail Queue"
      icon={QueueListIcon}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      action={<Link to="/queue" className="text-xs text-brand hover:underline">Manage</Link>}
    >
      {data && (
        <div>
          <div className="grid grid-cols-2 gap-3">
            {cells.map((c) => (
              <div key={c.label} className="rounded-lg border border-border bg-surface-raised/50 p-3">
                <p className="text-xs text-fg-muted">{c.label}</p>
                <p className={cn('mt-1 text-xl font-semibold', c.warn ? 'text-warning' : 'text-fg')}>
                  {formatCount(c.value)}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-fg-subtle">
            {oldestIso ? `Oldest deferred ${formatRelative(oldestIso)}` : 'No deferred backlog'}
          </p>
        </div>
      )}
    </WidgetCard>
  );
}
